import React, { CSSProperties, useRef, useState } from "react";
import { Button } from "Components/Button";
import { TextInput } from "Components/TextInput";
import { Field, FieldProps, Formik } from "formik";
import { useClickOutside } from "hooks";
import styled from "styled-components";
import { yupRequired } from "Utils/yup";
import * as yup from "yup";

interface Props {
  url: string;
  onSave: (url: string) => void;
  onRemove: () => void;
  onClose: () => void;
  style?: CSSProperties;
}

export const TOOLTIP_WIDTH = 420;

interface Values {
  url: string;
}

const VALIDATION_SCHEMA: yup.SchemaOf<Values> = yup
  .object()
  .shape({ url: yupRequired.url("Please provide a valid url") });

export const LinkTooltip: React.FC<Props> = ({
  url,
  onSave,
  onRemove,
  onClose,
  style,
}) => {
  const [mode, setMode] = useState<"view" | "edit">(url ? "view" : "edit");
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(tooltipRef, onClose);

  const handleSave = (values: Values) => {
    onSave(values.url);
  };

  return (
    <Container ref={tooltipRef} style={style}>
      <Formik
        initialValues={{ url }}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSave}
      >
        {({ handleSubmit }) => (
          <>
            {mode === "view" ? (
              <Url href={url} target="_blank">
                {url}
              </Url>
            ) : (
              <Field name="url">
                {({ field, meta }: FieldProps) => (
                  <TextInput
                    {...field}
                    placeholder="Enter URL..."
                    hasError={!!meta.error}
                    helperText={meta.error}
                    asString={false}
                  />
                )}
              </Field>
            )}
            <Actions>
              {mode === "view" ? (
                <>
                  <Button onClick={() => setMode("edit")} type="button" text>
                    Edit
                  </Button>
                  <Button onClick={onRemove} text>
                    Remove
                  </Button>
                </>
              ) : (
                <Button onClick={handleSubmit} text>
                  {url ? "Save" : "Insert"}
                </Button>
              )}
            </Actions>
          </>
        )}
      </Formik>
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 0;
  border-radius: 6px;
  background: #f2f5f7;
  padding: 15px 10px 5px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
  width: ${TOOLTIP_WIDTH}px;

  ${({ theme }) => theme.media.mobile} {
    max-width: 100%;
  }
`;

const Url = styled.a`
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: var(--text-color);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 5px;

  button {
    height: auto;
    padding: 5px 10px;
    width: auto;
  }
`;
